import React, {
  Fragment,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  BellIcon,
  CalendarDaysIcon,
  DocumentArrowUpIcon,
  FolderOpenIcon,
  HomeIcon,
  InboxArrowDownIcon,
  QueueListIcon,
  RectangleStackIcon,
  RocketLaunchIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon, PlusIcon, UserGroupIcon } from "@heroicons/react/20/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeadset } from "@fortawesome/free-solid-svg-icons";
import {
  APP_VERSION,
  clearLocalData,
  getAfspraken,
  getUser,
  getUserdetails,
  setAccesToken,
  setAppVersion,
  setUserdetails,
  signOutUser,
} from "../Localstorage/localstorage";
import UserDialog from "../pages/signin/UserDialog";
import { AXIOS_GET } from "../server/api/crud";
import Spinner from "../Componenten/Spinner";

import "moment/locale/nl-be";
import { AuthContext } from "../context/AuthContext";
import Passwoord from "../Componenten/Password/Passwoord";
import RegisterModal from "../pages/signin/RegisterModal";
import SmallSideBar from "./Structure/SmallSideBar";
import SideBar from "./Structure/SideBar";
import CSVReader from "../Componenten/CSVReader";
import RP_Avatar from "../Componenten/RP_Avatar";
import Calendly from "../Componenten/Calendly";
import { imgrepeat } from "../image";
import { useDispatch, useSelector } from "react-redux";
import { setAppointmentsCalendlyLoad } from "../redux/appointmentSlice";
import { fetchMailRooms } from "../apiCalls/messageApi";
import NotificationBellMenu from "../Componenten/Notifications/NotificationBellMenu";

const SupportAgentIcon = ({ className }) => <FontAwesomeIcon icon={faHeadset} className={className} />;

const navigation = (user, count) => [
  { name: "Dashboard", href: "/dashboard", icon: HomeIcon, current: true },
  {
    name: user.role === 1 ? "Patienten Dagboek" : "Mijn DagBoek",
    href: "/eetdagboek",
    icon: ScaleIcon,
    current: false,
  },
  {
    name: user.role === 1 ? "Patienten Vragenlijsten" : "Mijn Vragenlijsten",
    href: "/healthforms",
    icon: QueueListIcon,
    current: false,
  },
  {
    name: user.role === 1 ? "Afspraken" : "Mijn Afspraken",
    href: "/planning",
    icon: CalendarDaysIcon,
    current: false,
  },
  {
    name: user.role === 1 ? `Berichten` : "Mijn Berichten",
    href: "/mailbox",
    icon: InboxArrowDownIcon,
    current: false,
    count:count
  },
  {
    name: user.role === 1 ? "Patienten Documenten" : "Mijn Documenten",
    href: "/documents",
    icon: FolderOpenIcon,
    current: false,
    children: [
      /*
      {
        name: "Mijn Intakes",
        href: "/documents/intakes",
        icon: DocumentArrowDownIcon,
        current: false,
      },*/
      {
        name: user.role === 2 ? "Specialisten Adviezen" : "Patienten Adviezen",
        href: "/documents/advies",
        icon: BellIcon,
        current: false,
      },
      {
        name:
          user.role === 2
            ? "Mealplans/ Voedingsmiddelentabel/ Recepten"
            : "Patienten Mealplans/ Voedingsmiddelentabel/ Recepten",
        href: "/documents/plans",
        icon: RectangleStackIcon,
        current: false,
      },
      {
        name: user.role === 2 ? "Mijn Uploads" : "Patienten Opgeladen Documenten",
        href: "/documents/uploads",
        icon: DocumentArrowUpIcon,
        current: false,
      },
    ],
  },
  {
    name: user.role === 1 ? "Patienten Evoluties" : "Mijn Vooruitgang",
    href: "/evolution",
    icon: RocketLaunchIcon,
    current: false,
  },
  ...(user?.role === 2
    ? [
        {
          name: "Specialisten",
          href: "/admins",
          icon: UserGroupIcon,
          current: false,
        },
      ]
    : []),
  ...(user?.role === 1 && user?.hoofd_admin === 1
    ? [
        {
          name: "Gebruikers",
          href: "/users",
          icon: UserGroupIcon,
          current: false,
        },
      ]
    : []),
  {
    name: user.role === 1 ? "Support Inbox" : "Support",
    href: "/support",
    icon: SupportAgentIcon,
    current: false,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
const SIDEBAR_COLLAPSE_KEY = "rp-sidebar-collapsed";

const Layout = () => {
  const user = getUser();
  const appBgClass = "bg-wa-app";
  const userId = user?.id;
  const userdetail = getUserdetails();
  const userDetailsCacheId = userdetail?.id ?? userdetail?.user_id ?? null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1"
  );
  const [open, setOpen] = useState(false);
  const [openPasswoordModal, setOpenPasswoordModal] = useState(false);
  const [openAdminsModal, setopenAdminsModal] = useState(false);
  // const [openUsersModal, setopenUsersModal] = useState(false); // reserved for future use
  const [openCalendlyModal, setopenCalendlyModal] = useState(false);
  const [openInscrijvingen, setOpenInschrijvingen] = useState(false);
  const [afspraken, setAfsprakenCalendly] = useState(0);
  const [userdetails, setUserDetails] = useState(userdetail);
  const location = useLocation();
  const isMailboxRoute = location.pathname === "/mailbox";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Don't call getAfspraken() on every render - get it fresh inside callbacks when needed
  // This prevents unnecessary re-renders since JSON.parse creates a new array reference each time



  const { logout, login } = useContext(AuthContext); // Get the login function from AuthContext

  const invalidateStoredSession = useCallback(() => {
    signOutUser();
    logout();
    navigate("/signin", { replace: true });
  }, [logout, navigate]);
  const { appointmentsCalendly } = useSelector((state) => state.appointment);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);


  const toggleOpen = (name) => {
    setOpen((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  // Memoize userNavigation to prevent unnecessary re-renders
  const userNavigation = useMemo(() => [
    { name: "Profiel", href: null },
    { name: "Passwoord wijzigen", href: null },
    { name: "Uitloggen", href: null },
  ], []);

  const isUserDetailEmpty = (detail) => {
    return !detail || Object.keys(detail).length === 0;
  };

  const fetchData = useCallback(async (p_id) => {
    try {
      const response = await AXIOS_GET(`users/${p_id}`);
      const rows = response?.data?.data;
      if (!Array.isArray(rows) || rows.length === 0) {
        invalidateStoredSession();
        return;
      }
      const { first_name, last_name } = rows[0];
      setUserDetails(rows[0]);
      setUserdetails(rows[0]); //-- localstorage

      if (!first_name && !last_name) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    } catch (error) {
      // Ignore aborted/cancelled requests (navigation, reload)
      if (error.code === 'ECONNABORTED' || error.message === 'Request aborted' || error.message === 'canceled') {
        return;
      }
      const status = error.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        invalidateStoredSession();
        return;
      }
      console.error("Error fetching data:", error);
    }
  }, [invalidateStoredSession]);

  const fetchDataAfter = useCallback(async (p_id) => {
    try {
      const response = await AXIOS_GET(`users/${p_id}`);
      const rows = response?.data?.data;
      if (!Array.isArray(rows) || rows.length === 0) {
        invalidateStoredSession();
        return;
      }
      setUserDetails(rows[0]);
      setUserdetails(rows[0]); //-- localstorage
    } catch (error) {
      // Ignore aborted/cancelled requests (navigation, reload)
      if (error.code === 'ECONNABORTED' || error.message === 'Request aborted' || error.message === 'canceled') {
        return;
      }
      const status = error.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        invalidateStoredSession();
        return;
      }
      console.error("Error fetching data:", error);
    }
  }, [invalidateStoredSession]); // SignOUt is stable, setUserDetails and setUserdetails are state setters (stable)

  const fetchAfspraken = useCallback(() => {
    //console.log(appointmentsCalendly);
    // Get fresh allafspraken from localStorage inside the callback to avoid dependency issues
    const currentAfspraken = getAfspraken();
    if (appointmentsCalendly?.length > 0) {
      dispatch(setAppointmentsCalendlyLoad(appointmentsCalendly));
      setAfsprakenCalendly(currentAfspraken?.length);
    } else {
      dispatch(setAppointmentsCalendlyLoad(currentAfspraken));
      setAfsprakenCalendly(currentAfspraken?.length);
    }
  }, [appointmentsCalendly, dispatch]); // getAfspraken is called inside to avoid unstable deps

  // Prefetch mailrooms once so sidebar can show unread count
  useEffect(() => {
    if (!userId) return;
    fetchMailRooms(user, dispatch, null, 8, 0, "");
  }, [userId, dispatch]);

  useLayoutEffect(() => {
    if (!userId) {
      navigate("/signin");
      return;
    }

    if (isUserDetailEmpty(userdetail)) {
      fetchData(userId);
    }
    fetchAfspraken();
  }, [userId, userDetailsCacheId, fetchData, fetchAfspraken, navigate]);

  // Removed redundant fetchAfspraken useEffect - it's already called in useLayoutEffect
  // This was causing duplicate calls and unnecessary re-renders

  useEffect(() => {
    if (!userId || open) return;
    fetchDataAfter(userId);
  }, [open, userId, fetchDataAfter]);

  function SignOUt() {
    signOutUser();
    logout();
    clearLocalData();
    setAppVersion(APP_VERSION);
    setAccesToken("");
    navigate("/signin");
    // This forces the page to reload and bypass the cache
    window.location.reload(true);
  }

  if (userdetails)
    return (
      <div className={classNames("h-dvh text-wa-text", appBgClass)}>
        <UserDialog navigate={navigate} user={user} open={open} setOpen={setOpen} />
        <Passwoord
          user={user}
          open={openPasswoordModal}
          setOpen={setOpenPasswoordModal}
        />
        <RegisterModal open={openAdminsModal} setOpen={setopenAdminsModal} />
        <SmallSideBar
          Fragment={Fragment}
          user={user}
          location={location}
          navigation={navigation}
          setSidebarOpen={setSidebarOpen}
          sidebarOpen={sidebarOpen}
        />
        <CSVReader open={openInscrijvingen} setOpen={setOpenInschrijvingen} />
        <Calendly open={openCalendlyModal} setOpen={setopenCalendlyModal} />
        {/* Static sidebar for desktop */}
        <SideBar
          user={user}
          open={open}
          classNames={classNames}
          location={location}
          navigation={navigation}
          toggleOpen={toggleOpen}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
        />

        <div
          className={classNames(
            "h-max text-wa-text",
            sidebarCollapsed ? "lg:pl-28" : "lg:pl-80",
            appBgClass
          )}
        >
          <div className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-x-4 border-b border-wa-border bg-wa-panel2 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-wa-muted hover:text-wa-text lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div className="h-6 w-px bg-wa-border lg:hidden" aria-hidden="true" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <form className="relative flex flex-1" action="#" method="GET">
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
              </form>
              <div className="flex items-center gap-x-2 lg:gap-x-2">
                <NotificationBellMenu />
                {user?.role === 1 && (
                  <button
                    type="button"
                    onClick={() => setopenCalendlyModal(true)}
                    className="relative m-1 inline-flex h-10 items-center gap-x-2 rounded-xl border border-wa-border bg-wa-panel px-3 text-sm font-medium text-wa-text shadow-sm transition-colors hover:border-wa-accent/50 hover:bg-wa-hover sm:px-4"
                  >
                    {afspraken > 0 && (
                      <span className="absolute -right-1.5 -top-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold text-white">
                        {afspraken > 99 ? "99+" : afspraken}
                      </span>
                    )}
                    <img
                      alt="Calendly"
                      src={imgrepeat.calendly}
                      className="size-5"
                    />
                    <span className="hidden sm:inline">Boekingen via Calendly</span>
                    <span className="sm:hidden">Calendly</span>
                  </button>
                )}
                  {user?.hoofd_admin === 1 && user?.role === 1 && (
                    <button
                      type="button"
                      onClick={() => setopenAdminsModal(true)}
                      className="-m-0.5 inline-flex h-10 items-center gap-x-2 rounded-xl border border-transparent px-2 text-wa-muted transition-colors hover:border-wa-border hover:bg-wa-panel hover:text-wa-text sm:px-4"
                    >
                      <span className="sr-only">Personeel toevoegen</span>
                      <PlusIcon className="h-5 w-5 text-wa-accent" aria-hidden="true" />
                      <span className="hidden sm:inline">Personeel Toevoegen</span>
                    </button>)}
                   {/*  <button
                      type="button"
                      onClick={() => setOpenInschrijvingen(true)}
                      className="m-1 p-2 px-4 py-2 flex dark:text-white items-center gap-x-2 text-gray-900 hover:text-gray-500"
                    >
                      <span className="sr-only">View notifications</span>
                      <TableCellsIcon
                        className="h-5 w-5 text-gray-800 dark:text-white"
                        aria-hidden="true"
                      />
                      Van lijst ophalen
                    </button> */}
                

                {/* Separator */}
                <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-wa-border" aria-hidden="true" />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <RP_Avatar
                      avatar={userdetails?.avatar}
                      size={8}
                      alt={` ${userdetails?.full_name}`}
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        className="ml-4 text-sm font-semibold leading-6 text-wa-text"
                        aria-hidden="true"
                      >
                        {userdetails?.full_name || user?.username}
                      </span>
                      <ChevronDownIcon className="ml-2 h-5 w-5 text-wa-muted" aria-hidden="true" />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-56 origin-top-right rounded-lg border border-wa-border bg-wa-panel2 py-2 text-wa-text shadow-xl ring-1 ring-black/20 focus:outline-none">
                      {userNavigation.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <a
                              /*                               {...item.href && {href:item.href}} */
                              {...(item.name === "Uitloggen"
                                ? { onClick: () => SignOUt() }
                                : item.name === "Profiel"
                                ? {
                                    onClick: () => setOpen(true),
                                  }
                                : {
                                    onClick: () => setOpenPasswoordModal(true),
                                  })}
                              className={classNames(
                                active ? "bg-wa-hover" : "",
                                "block cursor-pointer px-3 py-2 text-sm leading-6 text-wa-text"
                              )}
                            >
                              {item.name}
                            </a>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          <div
            className={classNames(
              "text-wa-text",
              appBgClass,
              isMailboxRoute
                ? "flex h-[calc(100dvh-4rem)] min-h-0 flex-col overflow-hidden"
                : "h-lvh"
            )}
          >
            <Outlet />
          </div>
        </div>
      </div>
    );

  return <Spinner />;
};

export default Layout;
